import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import { isValidElementType } from 'react-is';
import React from 'react';
import classnames from 'classnames';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import uniq from 'lodash/uniq';
/**
 * A more robust React.createElement.
 * It can create elements from primitive values. A value that is a string,
 * number or array is considered a primitive value.
 * @param {React.ElementType<P>} Component A React element to create. Can be
 * either a tag name string (such as 'div' or 'span'), or a React component
 * type (a class or a function).
 * @param {(value: any) => Partial<P>} mapValueToProps A function that maps a
 * primitive value to the Component props.
 * @param {any} value A value used to create React element.
 * @param {Partial<P>} [options.defaultProps = {}] Default props object
 * @param {Partial<P> | (props: Partial<P>): Partial<P>} [options.overrideProps = {}]
 * Override props object or function. If a function is provided, it will be
 * invoked with regular properties.
 * @param {boolean} [options.autoGenerateKey = false] Whether or not automatic
 * key generation is allowed. Useful when rendering primitive values in a loop,
 * in which case the primitive value itself is used as the value of the
 * key property.
 * @template P
 * @returns {React.ElementType<P> | null}
 */

export default function createShorthand(Component, mapValueToProps, value) {
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};

  if (!isValidElementType(Component)) {
    throw new Error('createShorthand() Component must be a valid React element type.');
  } // Short circuit noop values


  if (isNil(value) || isBoolean(value)) {
    return null;
  }

  var isReactElement = /*#__PURE__*/React.isValidElement(value);
  var valueIsString = isString(value);
  var valueIsNumber = isNumber(value);
  var valueIsObject = isPlainObject(value);
  var isPrimitiveValue = valueIsString || valueIsNumber || isArray(value);
  /* eslint-disable no-console */
  // Return null for unhandled types

  if (!isReactElement && !valueIsObject && !isPrimitiveValue) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Shorthand value must be a String|Number|Array|Object|ReactElement. ' + "Received ".concat(typeof value));
    }

    return null;
  }
  /* eslint-enable no-console */
  // Build properties


  var _options$autoGenerate = options.autoGenerateKey,
      autoGenerateKey = _options$autoGenerate === void 0 ? false : _options$autoGenerate,
      _options$defaultProps = options.defaultProps,
      defaultProps = _options$defaultProps === void 0 ? {} : _options$defaultProps,
      _options$overrideProp = options.overrideProps,
      overrideProps = _options$overrideProp === void 0 ? {} : _options$overrideProp;
  var usersProps = isReactElement && value.props || valueIsObject && value || isPrimitiveValue && mapValueToProps(value);
  var computedOverrideProps = isFunction(overrideProps) ? overrideProps(_objectSpread(_objectSpread({}, defaultProps), usersProps)) : overrideProps; // Merge properties

  var props = _objectSpread(_objectSpread(_objectSpread({}, defaultProps), usersProps), computedOverrideProps); // Merge class names


  if (defaultProps.className || computedOverrideProps.className || usersProps.className) {
    var mergedClassesNames = classnames(defaultProps.className, computedOverrideProps.className, usersProps.className);
    props.className = uniq(mergedClassesNames.split(' ')).join(' ');
  } // Merge styles


  if (defaultProps.style || computedOverrideProps.style || usersProps.style) {
    props.style = _objectSpread(_objectSpread(_objectSpread({}, defaultProps.style), usersProps.style), computedOverrideProps.style);
  }
  /* eslint-disable react/prop-types */
  // Use key, childKey, or generate key


  if (isNil(props.key)) {
    var childKey = props.childKey;

    if (!isNil(childKey)) {
      // Apply and consume the childKey
      props.key = isFunction(childKey) ? childKey(props) : childKey;
      delete props.childKey;
    } else if (autoGenerateKey && (valueIsString || valueIsNumber)) {
      // Use string/number shorthand values as the key
      props.key = value;
    }
  }
  /* eslint-enable react/prop-types */
  // Create element


  return isReactElement ? /*#__PURE__*/React.cloneElement(value, props) : /*#__PURE__*/React.createElement(Component, props);
}